import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { arcadeUndergroundTitle, formatAchievementList } from "./gameHubUtils";

function formatReactionLabel(state, latestMs, bestMs, intensity) {
  const spicy = intensity === "spicy";
  if (state === "waiting") {
    return spicy
      ? "Heated: quicker signal, stricter timing — combo XP if you stay sharp."
      : "Wait for the card to flip hot green, then punch the click.";
  }
  if (state === "ready") return "Click now.";
  if (state === "false_start") return "Too early. Start a new round and wait for green.";
  if (state === "result" && latestMs != null) {
    return bestMs != null && bestMs !== latestMs
      ? `Reaction time: ${latestMs} ms. Best: ${bestMs} ms.`
      : `Reaction time: ${latestMs} ms.`;
  }
  return "Build a combo with consecutive sharp hits for bonus XP.";
}

function makeClientRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function authHeaders(sessionToken) {
  return {
    Authorization: `Bearer ${sessionToken}`,
    "Content-Type": "application/json",
  };
}

const arcadeCard = {
  padding: 18,
  marginBottom: 18,
};

function ArcadePanel({ apiBase, sessionToken, userName }) {
  const [board, setBoard] = useState(() => Array(9).fill(null));
  const [matchLabel, setMatchLabel] = useState("Your turn. You are X.");
  const [score, setScore] = useState({ wins: 0, losses: 0, draws: 0 });
  const [ticDifficulty, setTicDifficulty] = useState("standard");
  const [ticSession, setTicSession] = useState(null);

  const [reactionState, setReactionState] = useState("idle");
  const [latestReactionMs, setLatestReactionMs] = useState(null);
  const [bestReactionMs, setBestReactionMs] = useState(null);
  const [reactionIntensity, setReactionIntensity] = useState("normal");

  const [arcade, setArcade] = useState(null);
  const [arcadeBanner, setArcadeBanner] = useState("");
  const [questBusy, setQuestBusy] = useState(false);
  const [boardPulse, setBoardPulse] = useState(false);

  const reactionTimeoutRef = useRef(null);
  const reactionChallengeRef = useRef(null);

  const boardIsFull = useMemo(() => board.every(Boolean), [board]);
  const gameLocked = !ticSession || ticSession.status !== "playing" || boardIsFull;

  const loadArcadeProfile = useCallback(async () => {
    if (!sessionToken || !apiBase) {
      setArcade(null);
      return;
    }
    try {
      const r = await fetch(`${apiBase}/api/economy/dashboard`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      const j = await r.json();
      if (r.ok && j.profile?.arcade) setArcade(j.profile.arcade);
    } catch {
      setArcade(null);
    }
  }, [apiBase, sessionToken]);

  useEffect(() => {
    loadArcadeProfile();
  }, [loadArcadeProfile]);

  useEffect(() => {
    return () => {
      if (reactionTimeoutRef.current) window.clearTimeout(reactionTimeoutRef.current);
    };
  }, []);

  const startTicRound = useCallback(async () => {
    if (!sessionToken || !apiBase) return;
    try {
      const r = await fetch(`${apiBase}/api/arcade/tic/start`, {
        method: "POST",
        headers: authHeaders(sessionToken),
        body: JSON.stringify({
          difficulty: ticDifficulty,
        }),
      });
      const j = await r.json();
      if (!j.ok) {
        setArcadeBanner(j.error);
        window.setTimeout(() => setArcadeBanner(""), 5000);
        return;
      }
      setTicSession(j.session);
      setBoard(j.session?.board || Array(9).fill(null));
      setMatchLabel("Your turn. You are X.");
    } catch {
      setArcadeBanner("Could not start grid duel.");
      window.setTimeout(() => setArcadeBanner(""), 5000);
    }
  }, [apiBase, sessionToken, ticDifficulty]);

  useEffect(() => {
    setTicSession(null);
    setBoard(Array(9).fill(null));
    setMatchLabel("Start a duel. You are X.");
  }, [ticDifficulty]);

  const playMove = async (index) => {
    if (gameLocked || board[index] || !ticSession?.id || !sessionToken) return;
    try {
      const r = await fetch(`${apiBase}/api/arcade/tic/move`, {
        method: "POST",
        headers: authHeaders(sessionToken),
        body: JSON.stringify({
          sessionId: ticSession.id,
          index,
          clientRequestId: makeClientRequestId(),
        }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) {
        setArcadeBanner(j.error || "Move rejected.");
        window.setTimeout(() => setArcadeBanner(""), 5000);
        return;
      }
      const next = j.session || null;
      setTicSession(next?.status === "playing" ? next : null);
      setBoard(next?.board || Array(9).fill(null));
      if (j.finished) {
        if (j.winner === "X") {
          setMatchLabel(`${userName || "You"} take the grid.`);
          setScore((prev) => ({ ...prev, wins: prev.wins + 1 }));
          setArcadeBanner(`+${j.xpGained} rep · Grid win (${j.difficulty})${j.difficulty === "hard" ? " — clean read." : ""}`);
          setBoardPulse(true);
          window.setTimeout(() => setBoardPulse(false), 650);
          await loadArcadeProfile();
          window.setTimeout(() => setArcadeBanner(""), 4500);
        } else if (j.winner === "O") {
          setMatchLabel("Bot wins this round.");
          setScore((prev) => ({ ...prev, losses: prev.losses + 1 }));
        } else {
          setMatchLabel("Draw game.");
          setScore((prev) => ({ ...prev, draws: prev.draws + 1 }));
        }
      } else {
        setMatchLabel(
          j.session?.botMove != null
            ? "Bot moved. Your turn."
            : "Your turn."
        );
      }
    } catch {
      setArcadeBanner("Could not submit move.");
      window.setTimeout(() => setArcadeBanner(""), 5000);
    }
  };

  const startReactionRound = async () => {
    if (!sessionToken || !apiBase) {
      setReactionState("idle");
      return;
    }
    if (reactionTimeoutRef.current) {
      window.clearTimeout(reactionTimeoutRef.current);
    }
    setReactionState("waiting");
    setLatestReactionMs(null);
    reactionChallengeRef.current = null;
    try {
      const r = await fetch(`${apiBase}/api/arcade/reaction/start`, {
        method: "POST",
        headers: authHeaders(sessionToken),
        body: JSON.stringify({ intensity: reactionIntensity }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.error || "Could not start reaction round.");
      reactionChallengeRef.current = { id: j.challengeId, intensity: j.intensity };
      reactionTimeoutRef.current = window.setTimeout(() => {
        setReactionState("ready");
      }, Math.max(0, Number(j.waitMs) || 0));
    } catch (e) {
      setReactionState("idle");
      setArcadeBanner(e.message || "Could not start reaction round.");
      window.setTimeout(() => setArcadeBanner(""), 5000);
    }
  };

  const submitReactionXp = useCallback(
    async () => {
      if (!sessionToken || !apiBase) return;
      const ch = reactionChallengeRef.current;
      if (!ch?.id) return;
      try {
        const r = await fetch(`${apiBase}/api/arcade/reaction`, {
          method: "POST",
          headers: authHeaders(sessionToken),
          body: JSON.stringify({
            challengeId: ch.id,
            intensity: ch.intensity || reactionIntensity,
            clientRequestId: makeClientRequestId(),
          }),
        });
        const j = await r.json();
        if (j.ok) {
          setLatestReactionMs(j.ms);
          setBestReactionMs((prev) => (prev == null ? j.ms : Math.min(prev, j.ms)));
          const combo = j.comboBonus > 0 ? ` · combo +${j.comboBonus}` : "";
          setArcadeBanner(`+${j.xpGained} XP${combo} · streak ×${j.comboStreak || 0}`);
          await loadArcadeProfile();
          window.setTimeout(() => setArcadeBanner(""), 4000);
        } else if (j.error) {
          setArcadeBanner(j.error);
          window.setTimeout(() => setArcadeBanner(""), 5000);
        }
      } catch {
        /* ignore */
      } finally {
        reactionChallengeRef.current = null;
      }
    },
    [apiBase, sessionToken, loadArcadeProfile, reactionIntensity]
  );

  const stopReactionRound = () => {
    if (reactionTimeoutRef.current) {
      window.clearTimeout(reactionTimeoutRef.current);
      reactionTimeoutRef.current = null;
    }
  };

  const handleReactionClick = () => {
    if (reactionState === "waiting") {
      stopReactionRound();
      setReactionState("false_start");
      setLatestReactionMs(null);
      void submitReactionXp();
      return;
    }
    if (reactionState === "ready") {
      stopReactionRound();
      setReactionState("result");
      void submitReactionXp();
      return;
    }
    startReactionRound();
  };

  const claimDailyContract = async () => {
    if (!sessionToken || !apiBase || questBusy) return;
    setQuestBusy(true);
    try {
      const r = await fetch(`${apiBase}/api/arcade/daily-contract`, {
        method: "POST",
        headers: authHeaders(sessionToken),
        body: JSON.stringify({ clientRequestId: makeClientRequestId() }),
      });
      const j = await r.json();
      if (j.ok) {
        setArcadeBanner(`Crew contract cleared · +${j.xpGained} rep`);
        await loadArcadeProfile();
        window.setTimeout(() => setArcadeBanner(""), 6000);
      } else if (j.error) {
        setArcadeBanner(j.error);
        window.setTimeout(() => setArcadeBanner(""), 5000);
      }
    } catch {
      /* ignore */
    } finally {
      setQuestBusy(false);
    }
  };

  const reactionAccent =
    reactionState === "ready"
      ? "linear-gradient(135deg, rgba(42, 168, 90, 0.92), rgba(76, 175, 80, 0.72))"
      : reactionState === "false_start"
        ? "linear-gradient(135deg, rgba(208, 58, 58, 0.92), rgba(160, 32, 32, 0.72))"
        : reactionIntensity === "spicy"
          ? "linear-gradient(135deg, rgba(168, 85, 247, 0.92), rgba(239, 68, 68, 0.75))"
          : "linear-gradient(135deg, rgba(37, 99, 235, 0.9), rgba(88, 101, 242, 0.72))";

  const xpPct =
    arcade && arcade.xpForNextLevel > 0
      ? Math.min(100, Math.round((arcade.xpIntoLevel / arcade.xpForNextLevel) * 100))
      : 0;

  const questMask = arcade?.dailyQuestMask ?? 0;
  const questSteps = [
    { bit: 2, label: "Hit reflex" },
    { bit: 4, label: "Win grid duel" },
  ];

  const rankTitle = arcade?.level != null ? arcadeUndergroundTitle(arcade.level) : "";

  return (
    <div className="arcade-root game-hub-shell" style={{ padding: 20, overflowY: "auto", flex: 1, width: "100%" }}>
      <div style={{ width: "100%" }}>
        <h2 style={{ marginTop: 0, marginBottom: 6 }}>Neon row</h2>
        <p style={{ color: "var(--app-muted)", marginTop: 0, marginBottom: 16, lineHeight: 1.55, fontSize: 14 }}>
          Reflex and grid duels feed the same <strong>street rep</strong> (XP) as <strong>The pit</strong> floor games.
          False starts cost you — the crew gets live toasts when someone ranks up hard.
        </p>

        {!sessionToken ? (
          <p style={{ color: "#fda4af", marginBottom: 16, fontWeight: 600 }}>
            Sign in to earn rep, run streaks, clear crew contracts, and show up on the hall boards.
          </p>
        ) : null}

        {arcadeBanner ? (
          <div className="arcade-banner-toast" role="status">
            {arcadeBanner}
          </div>
        ) : null}

        {sessionToken && arcade ? (
          <section
            className={`hub-card hub-card-glow${boardPulse ? " arcade-hero-glow" : ""}`}
            style={{
              ...arcadeCard,
              border: "1px solid rgba(192, 132, 252, 0.28)",
              background: "linear-gradient(135deg, rgba(45, 18, 62, 0.55), rgba(12, 14, 22, 0.88))",
            }}
          >
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 11, color: "#c4b5fd", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 800 }}>
                  Street rep
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 10, marginTop: 4 }}>
                  <div style={{ fontSize: 26, fontWeight: 800 }}>
                    Level {arcade.level}
                    <span style={{ fontSize: 14, fontWeight: 600, color: "var(--app-muted)", marginLeft: 10 }}>
                      {arcade.totalXp} XP
                    </span>
                  </div>
                  {rankTitle ? (
                    <span className="hub-rank-pill" style={{ fontSize: 10 }}>
                      {rankTitle}
                    </span>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  loadArcadeProfile();
                }}
                style={{
                  borderRadius: 10,
                  border: "1px solid var(--app-border)",
                  background: "var(--app-input-bg)",
                  color: "var(--app-text)",
                  padding: "8px 14px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Refresh stats
              </button>
            </div>
            <div
              style={{
                marginTop: 12,
                height: 10,
                borderRadius: 999,
                background: "var(--app-input-bg)",
                overflow: "hidden",
                border: "1px solid var(--app-border)",
              }}
            >
              <div
                className="arcade-xp-bar-fill"
                style={{
                  height: "100%",
                  width: `${xpPct}%`,
                  background: "linear-gradient(90deg, #7c3aed, #fbbf24)",
                  transition: "width 0.4s ease",
                }}
              />
            </div>
            <div style={{ fontSize: 12, color: "var(--app-muted)", marginTop: 8, lineHeight: 1.45 }}>
              {arcade.xpIntoLevel} / {arcade.xpForNextLevel} to next rank · Grid wins{" "}
              {arcade.ticWinsRemainingToday} · Reflex tries {arcade.reactionRemainingToday}
              {arcade.reactionBestMs != null ? ` · PB ${arcade.reactionBestMs} ms` : ""}
              {arcade.reactionComboStreak > 0 ? ` · combo ×${arcade.reactionComboStreak}` : ""}
            </div>

            <div
              style={{
                marginTop: 14,
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px dashed rgba(250, 166, 26, 0.45)",
                background: "rgba(250, 166, 26, 0.06)",
              }}
            >
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Crew contract · +48 XP</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                {questSteps.map(({ bit, label }) => (
                  <span
                    key={bit}
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      padding: "6px 10px",
                      borderRadius: 999,
                      border: "1px solid var(--app-border)",
                      background: (questMask & bit) !== 0 ? "rgba(59,165,93,0.2)" : "var(--app-input-bg)",
                      color: (questMask & bit) !== 0 ? "#3ba55d" : "var(--app-muted)",
                    }}
                  >
                    {(questMask & bit) !== 0 ? "✓ " : "○ "}
                    {label}
                  </span>
                ))}
              </div>
              <button
                type="button"
                disabled={!arcade.dailyQuestReady || questBusy}
                onClick={claimDailyContract}
                style={{
                  borderRadius: 10,
                  border: "none",
                  background: arcade.dailyQuestReady ? "linear-gradient(90deg, #faa61a, #f47c2a)" : "var(--app-border)",
                  color: arcade.dailyQuestReady ? "#111" : "var(--app-muted)",
                  padding: "10px 16px",
                  cursor: arcade.dailyQuestReady && !questBusy ? "pointer" : "not-allowed",
                  fontWeight: 800,
                }}
              >
                {arcade.dailyQuestClaimed ? "Bagged today" : arcade.dailyQuestReady ? "Collect cut" : "Finish both"}
              </button>
            </div>

            {arcade.achievements?.length ? (
              <div style={{ marginTop: 10, fontSize: 12, color: "var(--app-text)", lineHeight: 1.45 }}>
                <span style={{ color: "var(--app-muted)", fontWeight: 700 }}>Notches · </span>
                {formatAchievementList(arcade.achievements).join(" · ")}
              </div>
            ) : null}
          </section>
        ) : null}

        <div
          style={{
            display: "grid",
            gap: 18,
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
          }}
        >
          <section
            className="hub-card"
            style={{
              ...arcadeCard,
              animation: boardPulse ? "arcade-board-pop 0.55s ease" : undefined,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 20 }}>Grid duel</h3>
                <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--app-muted)", lineHeight: 1.45 }}>
                  You vs the house bot — soft · even · perfect reads. Harder tiers pay more rep; ties waste your time.
                </p>
              </div>
              <button
                type="button"
                disabled={!sessionToken}
                onClick={startTicRound}
                style={{
                  borderRadius: 10,
                  border: "1px solid var(--app-border)",
                  background: "var(--app-input-bg)",
                  color: "var(--app-text)",
                  padding: "9px 12px",
                  cursor: sessionToken ? "pointer" : "not-allowed",
                  fontWeight: 600,
                }}
              >
                New duel
              </button>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              {["easy", "standard", "hard"].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setTicDifficulty(d)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 999,
                    border: ticDifficulty === d ? "2px solid var(--app-accent)" : "1px solid var(--app-border)",
                    background: ticDifficulty === d ? "var(--app-accent-muted, rgba(88,101,242,0.15))" : "var(--app-input-bg)",
                    color: "var(--app-text)",
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: "capitalize",
                  }}
                >
                  {d}
                </button>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10, marginBottom: 14 }}>
              {board.map((cell, index) => (
                <button
                  key={`cell-${index}`}
                  type="button"
                  onClick={() => playMove(index)}
                  disabled={gameLocked || Boolean(cell)}
                  className={!gameLocked && !cell ? "arcade-tile-hover" : ""}
                  style={{
                    aspectRatio: "1 / 1",
                    borderRadius: 14,
                    border: "1px solid var(--app-border)",
                    background: !gameLocked && !cell ? "var(--app-input-bg)" : "var(--app-elevated)",
                    color: "var(--app-text)",
                    fontSize: 28,
                    fontWeight: 800,
                    cursor: !gameLocked && !cell ? "pointer" : "default",
                    transition: "transform 0.12s ease, border-color 0.12s ease, background 0.12s ease",
                  }}
                >
                  {cell || ""}
                </button>
              ))}
            </div>

            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>{matchLabel}</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {["wins", "losses", "draws"].map((k) => (
                <div
                  key={k}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 999,
                    background: "var(--app-input-bg)",
                    border: "1px solid var(--app-border)",
                  }}
                >
                  {k}: {score[k]}
                </div>
              ))}
            </div>
          </section>

          <section className="hub-card" style={arcadeCard}>
            <div style={{ marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 20 }}>Reflex drill</h3>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--app-muted)", lineHeight: 1.45 }}>
                <strong>Heated</strong> = faster cue and bigger rep on clean hits — sloppy timing shreds your combo chain.
              </p>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              {[
                { id: "normal", label: "Calm" },
                { id: "spicy", label: "Heated" },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setReactionIntensity(id)}
                  disabled={reactionState === "waiting" || reactionState === "ready"}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 999,
                    border: reactionIntensity === id ? "2px solid var(--app-accent)" : "1px solid var(--app-border)",
                    background:
                      reactionIntensity === id ? "var(--app-accent-muted, rgba(88,101,242,0.15))" : "var(--app-input-bg)",
                    color: "var(--app-text)",
                    cursor: reactionState === "waiting" || reactionState === "ready" ? "not-allowed" : "pointer",
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={handleReactionClick}
              className={reactionState === "result" && latestReactionMs != null && latestReactionMs < 280 ? "arcade-react-spark" : ""}
              style={{
                width: "100%",
                minHeight: 200,
                borderRadius: 22,
                border: "1px solid rgba(255,255,255,0.08)",
                background: reactionAccent,
                color: "#fff",
                cursor: "pointer",
                padding: 24,
                textAlign: "left",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12)",
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                  opacity: 0.84,
                  marginBottom: 10,
                }}
              >
                {reactionState === "ready" ? "Click" : reactionState === "waiting" ? "Hold" : "Ready"}
              </div>
              <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.1, marginBottom: 12 }}>
                {reactionState === "result" && latestReactionMs != null
                  ? `${latestReactionMs} ms`
                  : reactionState === "ready"
                    ? "Now"
                    : reactionState === "waiting"
                      ? "Wait for green"
                      : reactionState === "false_start"
                        ? "False start"
                        : "Start round"}
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.5, maxWidth: 360 }}>
                {formatReactionLabel(reactionState, latestReactionMs, bestReactionMs, reactionIntensity)}
              </div>
            </button>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
              <button
                type="button"
                onClick={startReactionRound}
                style={{
                  borderRadius: 10,
                  border: "none",
                  background: "var(--app-accent)",
                  color: "#fff",
                  padding: "10px 14px",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                New round
              </button>
              <button
                type="button"
                onClick={() => {
                  stopReactionRound();
                  reactionChallengeRef.current = null;
                  setReactionState("idle");
                  setLatestReactionMs(null);
                  setBestReactionMs(null);
                }}
                style={{
                  borderRadius: 10,
                  border: "1px solid var(--app-border)",
                  background: "var(--app-input-bg)",
                  color: "var(--app-text)",
                  padding: "10px 14px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Clear best
              </button>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}

export default ArcadePanel;
